import { API_URL } from '@/config/api';
import React, { useState, useEffect, useRef } from 'react';
import { X, Send, Volume2, VolumeX } from 'lucide-react';
import axios from 'axios';
import { v4 as uuidv4 } from 'uuid';
import { useReaderCredits } from '../contexts/ReaderCreditContext';
import { PREVIEW_PROMO_CODE } from '@/constants/appConstants';

const API = API_URL;

const AudiaChatModal = ({ 
  isOpen, onClose, manuscriptId, manuscriptTitle = 'this book', 
  currentUser, currentChapterContext,
  isGuestSampleMode = false, previewQuestionCount = 0, 
  onPreviewQuestionAsked, onPreviewContinue 
}) => {
  // READER CREDIT CONTEXT: Ledger-derived balance (Phase 1)
  const { fetchBalance } = useReaderCredits();
  
  const [messages, setMessages] = useState([]);
  const [inputValue, setInputValue] = useState('');
  const [isResponding, setIsResponding] = useState(false);
  const messagesEndRef = useRef(null);
  const lastAudiaMessageRef = useRef(null);
  const inputRef = useRef(null);

  // Session tracking state
  const [sessionId, setSessionId] = useState(null);
  const [sessionStartTime, setSessionStartTime] = useState(null);
  const [questionCount, setQuestionCount] = useState(0);
  const [audiaMessageCount, setAudiaMessageCount] = useState(0);
  const [totalAEU, setTotalAEU] = useState(0);
  const [totalCredits, setTotalCredits] = useState(0); // Credits = AEU * 8 (rounded up)
  
  // Session summary modal (temporary for debugging)
  const [showSummaryModal, setShowSummaryModal] = useState(false);
  const [sessionSummary, setSessionSummary] = useState(null);
  
  // Phase 3: TTS state
  const [speakResponses, setSpeakResponses] = useState(true); // Default to true
  const [currentAudio, setCurrentAudio] = useState(null);
  const audioRef = useRef(null);
  
  // Response length preference: 'brief' (300 chars) or 'detailed' (750 chars)
  const [responseLength, setResponseLength] = useState('brief'); // Default to brief
  
  // Closing state
  const [isClosing, setIsClosing] = useState(false);

  // Contextual question suggestions
  const [suggestions, setSuggestions] = useState([]);

  // Track if welcome TTS has been initiated to prevent race conditions
  const welcomeTTSInitiated = useRef(false);
  
  // Track active polling intervals for cleanup
  const activePollingRef = useRef(null);

  // Track mounted state to prevent audio play after unmount/close
  const isOpenRef = useRef(false);
  
  // Track welcome TTS timeout for cancellation
  const welcomeTTSTimeoutRef = useRef(null);
  
  // Track the manuscript ID we initialized for to prevent unnecessary resets
  const initializedForRef = useRef(null);

  // Reset messages when modal opens with a NEW book (not on every re-render)
  useEffect(() => {
    if (isOpen && manuscriptTitle) {
      // Only reset if we're opening for a different manuscript
      const shouldReset = initializedForRef.current !== manuscriptId;
      
      if (shouldReset) {
        console.log('[AUDIA] Initializing chat for manuscript:', manuscriptId);
        initializedForRef.current = manuscriptId;
        
        // LOCKED STATE: guest has already used 3 questions — show only conversion message
        if (isGuestSampleMode && previewQuestionCount >= 3) {
          setMessages([
            {
              id: 1,
              from: 'conversion',
              text: `We hope you're enjoying your immersive reading experience with NewAudia. Click 'Continue' and use the Promo Code: ${PREVIEW_PROMO_CODE} to start with 25 FREE Reader Credits.`,
            },
          ]);
          setInputValue('');
          setSuggestions([]);
          return;
        }
        
        // Reset welcome TTS flag for new book
        welcomeTTSInitiated.current = false;
        
        // Build welcome message with chapter context (no asterisks)
        let welcomeText;
        if (isGuestSampleMode) {
          welcomeText = `Hi, I'm Audia — your AI reading companion. You're previewing "${manuscriptTitle}". Unlock 25 FREE Reader Credits when you ask me three questions about this book.`;
        } else {
          welcomeText = `Hi, I'm Audia. You're currently reading "${manuscriptTitle}".`;
        
        // Add chapter context if available
        if (currentChapterContext?.chapterTitle) {
          welcomeText += ` You're on "${currentChapterContext.chapterTitle}"`;
          if (currentChapterContext.totalChapters > 0) {
            welcomeText += ` (Segment ${(currentChapterContext.chapterIndex || 0) + 1} of ${currentChapterContext.totalChapters} NFD Segments in this book)`;
          }
          welcomeText += `.`;
        }
        
        welcomeText += ` Ask me anything about the story, characters, or themes — I can also help with questions about your current segment.`;
        }
        
        // Reset messages with the new book's welcome message
        setMessages([
          {
            id: 1,
            from: 'audia',
            text: welcomeText,
            audioUrl: null,
            audioStatus: null,
          },
        ]);
        // Reset session state for new conversation
        setSessionId(null);
        setQuestionCount(0);
        setAudiaMessageCount(0);
        setTotalAEU(0);
        setTotalCredits(0);
        setInputValue('');
        setSuggestions([]);
        
        // Fetch contextual welcome suggestions
        (async () => {
          try {
            const token = localStorage.getItem('accessToken');
            const res = await axios.post(`${API}/api/audia/chat/suggestions/welcome`, {
              manuscriptId: manuscriptId,
              chapter_context: currentChapterContext ? {
                chapter_index: currentChapterContext.chapterIndex,
                chapter_title: currentChapterContext.chapterTitle,
                total_chapters: currentChapterContext.totalChapters
              } : null
            }, {
              headers: token ? { Authorization: `Bearer ${token}` } : {}
            });
            if (res.data?.suggestions?.length > 0) {
              setSuggestions(res.data.suggestions);
              console.log('[AUDIA] Welcome suggestions loaded:', res.data.suggestions);
            }
          } catch (err) {
            console.warn('[AUDIA] Welcome suggestions fetch failed (non-fatal):', err);
          }
        })();
        
        // Generate welcome TTS immediately after setting message
        // This ensures we have the welcome text and avoids race condition
        if (speakResponses && !welcomeTTSInitiated.current) {
          welcomeTTSInitiated.current = true;
          // Small delay to ensure state is updated — tracked for cancellation on close
          welcomeTTSTimeoutRef.current = setTimeout(() => {
            if (isOpenRef.current) {
              generateWelcomeTTSWithText(welcomeText);
            }
          }, 100);
        }
      }
    } else if (!isOpen) {
      // Reset the initialized ref when modal closes so it re-initializes on next open
      initializedForRef.current = null;
    }
  }, [isOpen, manuscriptId, manuscriptTitle, speakResponses, isGuestSampleMode, previewQuestionCount]);

  // Scroll to the top of the latest Audia response (not the bottom of the chat)
  useEffect(() => {
    if (messages.length === 0) return;
    const lastMsg = messages[messages.length - 1];
    if (lastMsg.from === 'audia' && lastAudiaMessageRef.current) {
      // Scroll so the top of Audia's response is visible
      lastAudiaMessageRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
    } else {
      // For user messages, scroll to bottom to show what they just sent
      messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    }
  }, [messages]);

  // Initialize session when modal opens, cleanup when it closes
  useEffect(() => {
    if (isOpen) {
      isOpenRef.current = true;
      // Generate new session ID if not already set
      if (!sessionId) {
        const newSessionId = `session_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
        setSessionId(newSessionId);
        setSessionStartTime(new Date().toISOString());
        console.log('[AUDIA SESSION] Started:', newSessionId, 'for manuscript:', manuscriptId);
      }
      
      // Focus input
      if (inputRef.current) {
        setTimeout(() => inputRef.current?.focus(), 100);
      }
    } else {
      isOpenRef.current = false;
      // Modal is closing - stop any audio playback immediately
      stopAudioPlayback();
      // Cancel pending welcome TTS timeout
      if (welcomeTTSTimeoutRef.current) {
        clearTimeout(welcomeTTSTimeoutRef.current);
        welcomeTTSTimeoutRef.current = null;
      }
      // Reset welcome TTS flag for next open
      welcomeTTSInitiated.current = false;
      // Clear any active polling
      if (activePollingRef.current) {
        clearInterval(activePollingRef.current);
        activePollingRef.current = null;
      }
    }
  }, [isOpen]); // Simplified dependencies

  // Cleanup on unmount — catches cases where parent unmounts the component entirely
  useEffect(() => {
    return () => {
      isOpenRef.current = false;
      if (audioRef.current) {
        audioRef.current.pause();
        audioRef.current.src = '';
        audioRef.current = null;
      }
      if (activePollingRef.current) {
        clearInterval(activePollingRef.current);
        activePollingRef.current = null;
      }
      if (welcomeTTSTimeoutRef.current) {
        clearTimeout(welcomeTTSTimeoutRef.current);
        welcomeTTSTimeoutRef.current = null;
      }
    };
  }, []);

  // NEW: Poll for TTS audio status (async, non-blocking)
  const pollForAudioStatus = async (answerId) => {
    // Clear any previous polling
    if (activePollingRef.current) {
      clearInterval(activePollingRef.current);
    }
    
    const maxAttempts = 20; // Max 20 attempts = 30 seconds
    let attempts = 0;

    const pollInterval = setInterval(async () => {
      attempts++;
      
      if (attempts > maxAttempts) {
        console.warn('[AUDIA TTS] Polling timeout for', answerId);
        clearInterval(pollInterval);
        activePollingRef.current = null;
        // Update message to show failed status
        setMessages(prev => prev.map(msg => 
          msg.id === answerId ? { ...msg, audioStatus: 'failed' } : msg
        ));
        return;
      }

      try {
        const token = localStorage.getItem('accessToken');
        const response = await axios.get(`${API}/api/audia/answer-audio-status`, {
          params: { answerId },
          headers: token ? { Authorization: `Bearer ${token}` } : {}
        });

        const { ttsStatus, audioBase64 } = response.data;

        if (ttsStatus === 'ready' && audioBase64) {
          clearInterval(pollInterval);
          activePollingRef.current = null;
          console.log('[AUDIA TTS] Audio ready for', answerId);

          // Convert base64 to Blob
          try {
            const audioData = atob(audioBase64);
            const audioArray = new Uint8Array(audioData.length);
            for (let i = 0; i < audioData.length; i++) {
              audioArray[i] = audioData.charCodeAt(i);
            }
            const audioBlob = new Blob([audioArray], { type: 'audio/mpeg' });
            const audioUrl = URL.createObjectURL(audioBlob);

            // Update message with audio URL
            setMessages(prev => prev.map(msg => 
              msg.id === answerId ? { ...msg, audioUrl, audioStatus: 'ready' } : msg
            ));

            // CRITICAL: Stop any currently playing audio before starting new one
            if (audioRef.current) {
              audioRef.current.pause();
              audioRef.current.currentTime = 0;
            }

            // Only auto-play if the modal is still open
            if (!isOpenRef.current) {
              console.log('[AUDIA TTS] Modal closed, skipping autoplay for', answerId);
              URL.revokeObjectURL(audioUrl);
              return;
            }

            // Auto-play immediately
            const audio = new Audio(audioUrl);
            audioRef.current = audio;
            
            // Update status to "playing" when playback starts
            audio.onplay = () => {
              setMessages(prev => prev.map(msg => 
                msg.id === answerId ? { ...msg, audioStatus: 'playing' } : msg
              ));
            };
            
            // Reset status when playback ends
            audio.onended = () => {
              setMessages(prev => prev.map(msg => 
                msg.id === answerId ? { ...msg, audioStatus: 'ready' } : msg
              ));
            };
            
            // Force autoplay
            try {
              await audio.play();
              console.log('[AUDIA TTS] ✓ Auto-playing response');
            } catch (playError) {
              console.error('[AUDIA TTS] Autoplay blocked by browser:', playError);
              // Fallback: user needs to click play button
            }
          } catch (err) {
            console.error('[AUDIA TTS] Failed to process audio:', err);
            setMessages(prev => prev.map(msg => 
              msg.id === answerId ? { ...msg, audioStatus: 'failed' } : msg
            ));
          }
        } else if (ttsStatus === 'failed') {
          clearInterval(pollInterval);
          activePollingRef.current = null;
          console.warn('[AUDIA TTS] Audio generation failed for', answerId);
          setMessages(prev => prev.map(msg => 
            msg.id === answerId ? { ...msg, audioStatus: 'failed' } : msg
          ));
        }
        // If still "pending", continue polling
      } catch (error) {
        console.error('[AUDIA TTS] Polling error:', error);
        // Don't stop polling on network errors, might recover
      }
    }, 1500); // Poll every 1.5 seconds
    
    // Track the interval for cleanup
    activePollingRef.current = pollInterval;
  };

  const handleSendMessage = async (directMessage) => {
    const trimmedInput = (directMessage || inputValue).trim();
    if (!trimmedInput || isResponding || !manuscriptId) return;

    // Block if preview limit reached
    if (isGuestSampleMode && previewQuestionCount >= 3) return;

    // Clear suggestions when user sends a message
    setSuggestions([]);

    // Stop any currently playing audio to focus on new question
    stopAudioPlayback();

    // Add user message
    const userMessage = {
      id: Date.now(),
      from: 'user',
      text: trimmedInput,
    };
    setMessages((prev) => [...prev, userMessage]);
    setInputValue('');
    setIsResponding(true);

    // PREVIEW MODE: Use lightweight preview endpoint (no auth, no credits)
    if (isGuestSampleMode) {
      try {
        const chatHistory = messages
          .filter((msg) => msg.from !== 'audia' || msg.id !== 1)
          .map((msg) => ({
            role: msg.from === 'user' ? 'user' : 'assistant',
            content: msg.text,
          }));

        const response = await axios.post(`${API}/api/audia/chat/preview-message`, {
          manuscript_id: manuscriptId,
          message: trimmedInput,
          chat_history: chatHistory,
          include_tts: speakResponses
        });

        const previewAnswerId = response.data.answer_id || (Date.now() + 1);
        const audiaMessage = {
          id: previewAnswerId,
          from: 'audia',
          text: response.data.response,
          audioUrl: null,
          audioStatus: speakResponses ? 'preparing' : null,
        };
        setMessages((prev) => [...prev, audiaMessage]);

        // Start TTS polling if audio was requested
        if (speakResponses && response.data.answer_id) {
          pollForAudioStatus(response.data.answer_id);
        }

        // Only increment counter on successful LLM responses (success: true)
        if (response.data.success !== false) {
          const newCount = previewQuestionCount + 1;

          if (newCount < 3) {
            // Questions 1 & 2: show suggestion chips, THEN notify parent
            if (response.data.suggestions?.length > 0) {
              setSuggestions(response.data.suggestions);
            }
            if (onPreviewQuestionAsked) onPreviewQuestionAsked();
          } else {
            // Question 3: append conversion message, no chips, THEN notify parent
            setSuggestions([]);
            const conversionMessage = {
              id: Date.now() + 2,
              from: 'conversion',
              text: `We hope you're enjoying your immersive reading experience with NewAudia. Click 'Continue' and use the Promo Code: ${PREVIEW_PROMO_CODE} to start with 25 FREE Reader Credits.`,
            };
            setMessages((prev) => [...prev, conversionMessage]);
            if (onPreviewQuestionAsked) onPreviewQuestionAsked();
          }
        }
      } catch (error) {
        console.error('[AUDIA PREVIEW] Error:', error);
        const fallbackMessage = {
          id: Date.now() + 1,
          from: 'audia',
          text: "I'm having trouble connecting right now — let's try that again.",
        };
        setMessages((prev) => [...prev, fallbackMessage]);
        setSuggestions([]);
      } finally {
        setIsResponding(false);
      }
      return;
    }

    // AUTHENTICATED MODE: existing flow below
    // Generate client_event_id for retry-safe idempotency (MANDATORY)
    const clientEventId = uuidv4();

    try {
      // Build chat history for API (convert to required format)
      const chatHistory = messages
        .filter((msg) => msg.from !== 'audia' || msg.id !== 1) // Exclude initial greeting
        .map((msg) => ({
          role: msg.from === 'user' ? 'user' : 'audia',
          content: msg.text,
        }));

      // Call real Audia API with authentication
      const token = localStorage.getItem('accessToken');
      const response = await axios.post(`${API}/api/audia/chat/message`, {
        manuscriptId: manuscriptId,
        message: trimmedInput,
        chatHistory: chatHistory,
        include_tts: speakResponses, // Phase 3: Request TTS if enabled
        client_event_id: clientEventId, // MANDATORY: Retry-safe idempotency key
        response_length: responseLength, // 'brief' (300 chars) or 'detailed' (750 chars)
        // Chapter context for position-aware responses
        chapter_context: currentChapterContext ? {
          chapter_index: currentChapterContext.chapterIndex,
          chapter_title: currentChapterContext.chapterTitle,
          total_chapters: currentChapterContext.totalChapters
        } : null
      }, {
        headers: {
          Authorization: `Bearer ${token}`
        }
      });

      // STEP 1: Display text answer immediately
      const answerId = response.data.answerId;
      const audiaMessage = {
        id: answerId || Date.now() + 1,
        from: 'audia',
        text: response.data.audiaResponse,
        audioUrl: null,
        audioStatus: speakResponses ? 'preparing' : null, // Show "preparing" if audio enabled
      };

      setMessages((prev) => [...prev, audiaMessage]);
      
      // Update suggestions from response
      if (response.data.suggestions?.length > 0) {
        setSuggestions(response.data.suggestions);
      } else {
        setSuggestions([]);
      }
      
      // STEP 2: Start polling for TTS if enabled (async, non-blocking)
      if (speakResponses && answerId) {
        console.log('[AUDIA TTS] Starting async audio polling for', answerId);
        pollForAudioStatus(answerId);
      }
      
      // Track session metrics
      setQuestionCount((prev) => prev + 1);
      setAudiaMessageCount((prev) => prev + 1);
      const aeuGenerated = response.data.euGenerated || 0;
      setTotalAEU((prev) => prev + aeuGenerated);
      // Calculate credits: AEU * 8, rounded up per response
      setTotalCredits((prev) => prev + Math.ceil(aeuGenerated * 8));
      
      // PHASE 1: Refresh ledger-derived balance after debit
      // Pattern A: Fetch updated balance after successful interaction
      fetchBalance().catch(err => console.warn('[CREDITS] Balance refresh failed:', err));
      
    } catch (error) {
      console.error('[AUDIA] Error calling chat API:', error);
      
      // Graceful fallback
      const fallbackMessage = {
        id: Date.now() + 1,
        from: 'audia',
        text: "I'm having trouble connecting right now — let's try that again.",
      };
      setMessages((prev) => [...prev, fallbackMessage]);
      setAudiaMessageCount((prev) => prev + 1);  // Count fallback too
      setSuggestions([]);
    } finally {
      setIsResponding(false);
    }
  };

  const generateWelcomeTTS = async () => {
    try {
      const token = localStorage.getItem('accessToken');
      const welcomeText = messages[0]?.text;
      
      if (!welcomeText) {
        console.warn('[AUDIA TTS] No welcome text available');
        return;
      }
      
      console.log('[AUDIA TTS] Generating welcome message audio...');
      
      const response = await axios.post(`${API}/api/audia/tts`, {
        text: welcomeText,
      }, {
        headers: {
          Authorization: `Bearer ${token}`
        }
      });
      
      if (response.data.enabled && response.data.audioBase64) {
        try {
          // Convert base64 to Blob and create Object URL
          const audioData = atob(response.data.audioBase64);
          const audioArray = new Uint8Array(audioData.length);
          for (let i = 0; i < audioData.length; i++) {
            audioArray[i] = audioData.charCodeAt(i);
          }
          const audioBlob = new Blob([audioArray], { type: 'audio/mpeg' });
          const audioUrl = URL.createObjectURL(audioBlob);
          
          // Update welcome message with audio
          setMessages(prev => prev.map((msg, idx) => 
            idx === 0 ? { ...msg, audioUrl } : msg
          ));
          
          // CRITICAL: Stop any currently playing audio
          if (audioRef.current) {
            audioRef.current.pause();
            audioRef.current.currentTime = 0;
          }
          
          // Auto-play welcome message
          const audio = new Audio(audioUrl);
          audioRef.current = audio;
          
          try {
            await audio.play();
            console.log('[AUDIA TTS] ✓ Auto-playing welcome message');
          } catch (playError) {
            console.error('[AUDIA TTS] Welcome autoplay blocked by browser:', playError);
            // Fallback: user can manually click play
          }
        } catch (err) {
          console.error('[AUDIA TTS] Failed to process welcome audio:', err);
        }
      }
    } catch (err) {
      console.error('[AUDIA TTS] Failed to generate welcome TTS:', err);
    }
  };

  // Generate welcome TTS with text passed directly (avoids race condition with state)
  const generateWelcomeTTSWithText = async (welcomeText) => {
    try {
      const token = localStorage.getItem('accessToken');
      
      console.log('[AUDIA TTS] Generating welcome message audio for:', welcomeText.substring(0, 50) + '...');
      
      const response = await axios.post(`${API}/api/audia/tts`, {
        text: welcomeText,
      }, {
        headers: {
          Authorization: `Bearer ${token}`
        }
      });
      
      if (response.data.enabled && response.data.audioBase64) {
        try {
          // Convert base64 to Blob and create Object URL
          const audioData = atob(response.data.audioBase64);
          const audioArray = new Uint8Array(audioData.length);
          for (let i = 0; i < audioData.length; i++) {
            audioArray[i] = audioData.charCodeAt(i);
          }
          const audioBlob = new Blob([audioArray], { type: 'audio/mpeg' });
          const audioUrl = URL.createObjectURL(audioBlob);
          
          // Update welcome message with audio
          setMessages(prev => prev.map((msg, idx) => 
            idx === 0 ? { ...msg, audioUrl } : msg
          ));
          
          // CRITICAL: Stop any currently playing audio
          if (audioRef.current) {
            audioRef.current.pause();
            audioRef.current.currentTime = 0;
          }
          
          // Auto-play welcome message
          const audio = new Audio(audioUrl);
          audioRef.current = audio;
          
          try {
            await audio.play();
            console.log('[AUDIA TTS] ✓ Auto-playing welcome message');
          } catch (playError) {
            console.error('[AUDIA TTS] Welcome autoplay blocked by browser:', playError);
          }
        } catch (err) {
          console.error('[AUDIA TTS] Failed to process welcome audio:', err);
        }
      }
    } catch (err) {
      console.error('[AUDIA TTS] Failed to generate welcome TTS:', err);
    }
  };

  const stopAudioPlayback = () => {
    if (audioRef.current) {
      audioRef.current.pause();
      audioRef.current.currentTime = 0;
      // Revoke object URL to free memory and abort any pending audio fetch
      if (audioRef.current.src && audioRef.current.src.startsWith('blob:')) {
        URL.revokeObjectURL(audioRef.current.src);
      }
      audioRef.current.src = '';
      audioRef.current = null;
      console.log('[AUDIA TTS] Audio stopped, resources released');
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  };

  const handleSuggestionTap = (question) => {
    if (isResponding) return;
    setInputValue('');
    handleSendMessage(question);
  };

  const handleCloseSession = async () => {
    // Stop any playing audio immediately
    stopAudioPlayback();
    
    // Show spinner
    setIsClosing(true);
    
    // Close immediately, send summary in background
    setTimeout(() => {
      onClose();
      setIsClosing(false);
    }, 100);
    
    // Send session summary to backend (non-blocking)
    if (sessionId && sessionStartTime) {
      const endTime = new Date().toISOString();
      const duration = (new Date(endTime) - new Date(sessionStartTime)) / 1000;
      
      const chatHistory = messages
        .filter((msg) => msg.id !== 1)
        .map((msg) => ({
          role: msg.from === 'user' ? 'user' : 'audia',
          content: msg.text,
        }));
      
      const summaryData = {
        sessionId: sessionId,
        manuscriptId: manuscriptId,
        manuscriptTitle: manuscriptTitle,
        questionCount: questionCount,
        audiaMessageCount: audiaMessageCount,
        activeEngagementUnits: totalAEU,
        creditsUsed: totalCredits,  // Credits = AEU * 8
        startedAt: sessionStartTime,
        endedAt: endTime,
        durationSeconds: Math.round(duration),
        userRole: 'reader',
        chatHistory: chatHistory,
      };
      
      // Send in background, don't await
      const token = localStorage.getItem('accessToken');
      axios.post(`${API}/api/audia/chat/end-session`, summaryData, {
          headers: token ? { Authorization: `Bearer ${token}` } : {}
        })
        .then(() => {
          console.log('[AUDIA SESSION] Ended:', sessionId, {
            questions: questionCount,
            responses: audiaMessageCount,
            aeu: totalAEU,
            credits: totalCredits,
          });
        })
        .catch(error => {
          console.error('[AUDIA SESSION] Error ending session:', error);
        });
    }
  };
  
  const handleDismissSummary = () => {
    // Reset session state
    setSessionId(null);
    setSessionStartTime(null);
    setQuestionCount(0);
    setAudiaMessageCount(0);
    setTotalAEU(0);
    setTotalCredits(0);
    setSessionSummary(null);
    setShowSummaryModal(false);
    
    // Call parent onClose
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center bg-black/60 backdrop-blur-sm sm:p-4">
      {/* Modal Container - Full screen on mobile, centered dialog on desktop */}
      <div className="relative w-full sm:max-w-md h-[100dvh] sm:h-[90vh] sm:max-h-[720px] bg-gradient-to-br from-slate-900 to-slate-800 sm:rounded-2xl shadow-2xl border-0 sm:border sm:border-slate-700 flex flex-col overflow-hidden">
        {/* Header with Logo and Avatar */}
        <div className="flex items-center justify-between px-4 py-3 bg-gradient-to-r from-cyan-600 to-blue-600 border-b border-cyan-500/30">
          <div className="flex items-center gap-3">
            <img 
              src="/newaudia-logo.jpg" 
              alt="NewAudia" 
              className="h-8 object-contain"
            />
            <div className="flex items-center gap-2">
              <img 
                src="/audia-avatar.jpg" 
                alt="Audia" 
                className="w-8 h-8 rounded-full object-cover border-2 border-white/50"
              />
              <div className="flex flex-col">
                <span className="text-white font-semibold text-base">Talk With Audia</span>
                <span className="text-white/90 text-sm">{manuscriptTitle}</span>
              </div>
            </div>
          </div>
          <button
            onClick={handleCloseSession}
            className="text-white hover:bg-white/20 rounded-full p-1 transition-colors"
            aria-label="Close chat"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Chat Body - Scrollable */}
        <div className="flex-1 overflow-y-auto p-4 space-y-4">
          {messages.map((msg, idx) => (
            <div
              key={msg.id}
              ref={(msg.from === 'audia' || msg.from === 'conversion') && idx === messages.length - 1 ? lastAudiaMessageRef : null}
              className={`flex ${msg.from === 'user' ? 'justify-end' : 'justify-start'}`}
            >
              {msg.from === 'conversion' ? (
                <div className="max-w-[90%] rounded-2xl px-5 py-4 bg-gradient-to-br from-[#1B3A6B] to-[#0f2847] border border-teal-500/30 shadow-lg">
                  <p className="text-sm leading-relaxed text-white/90">{msg.text}</p>
                  <button
                    data-testid="preview-continue-btn"
                    onClick={() => { if (onPreviewContinue) onPreviewContinue(); }}
                    className="mt-3 w-full py-2.5 bg-[#00A896] hover:bg-[#00C4AE] text-white font-semibold rounded-xl text-sm transition-colors active:scale-[0.98]"
                  >
                    Continue
                  </button>
                </div>
              ) : (
              <div
                className={`max-w-[80%] rounded-2xl px-4 py-2.5 ${
                  msg.from === 'user'
                    ? 'bg-gradient-to-br from-purple-600 to-blue-600 text-white'
                    : 'bg-slate-700/80 text-slate-100 border border-slate-600/50'
                }`}
              >
                {msg.from === 'audia' && (
                  <div className="flex items-center justify-between gap-2 mb-1">
                    <div className="flex items-center gap-2">
                      <img 
                        src="/audia-avatar.jpg" 
                        alt="Audia" 
                        className="w-5 h-5 rounded-full object-cover border border-cyan-400"
                      />
                      <span className="text-xs text-cyan-400 font-semibold">Audia</span>
                    </div>
                    {/* Audio status indicator */}
                    <div className="flex items-center gap-1">
                      {msg.audioStatus === 'preparing' && (
                        <span className="text-xs text-slate-400 italic">Preparing audio...</span>
                      )}
                      {msg.audioStatus === 'playing' && (
                        <span className="text-xs text-green-400 flex items-center gap-1">
                          <Volume2 className="w-3 h-3 animate-pulse" />
                          Playing
                        </span>
                      )}
                      {msg.audioStatus === 'failed' && (
                        <span className="text-xs text-red-400">Audio unavailable</span>
                      )}
                      {msg.audioUrl && msg.audioStatus !== 'playing' && (
                        <button
                          onClick={() => {
                            // Stop any currently playing audio
                            if (audioRef.current) {
                              audioRef.current.pause();
                              audioRef.current.currentTime = 0;
                            }
                            
                            const audio = new Audio(msg.audioUrl);
                            audioRef.current = audio;
                            audio.play().catch(err => console.error('[AUDIA TTS] Play failed:', err));
                          }}
                          className="text-cyan-400 hover:text-cyan-300 transition-colors"
                          title="Play audio"
                        >
                          <Volume2 className="w-4 h-4" />
                        </button>
                      )}
                    </div>
                  </div>
                )}
                <p className="text-sm leading-relaxed whitespace-pre-wrap">{msg.text}</p>
              </div>
              )}
            </div>
          ))}

          {/* Typing indicator */}
          {isResponding && (
            <div className="flex justify-start">
              <div className="max-w-[80%] rounded-2xl px-4 py-2.5 bg-slate-700/80 border border-slate-600/50">
                <div className="flex items-center gap-2 mb-1">
                  <img 
                    src="/audia-avatar.jpg" 
                    alt="Audia" 
                    className="w-5 h-5 rounded-full object-cover border border-cyan-400"
                  />
                  <span className="text-xs text-cyan-400 font-semibold">Audia</span>
                </div>
                <div className="flex gap-1">
                  <div className="w-2 h-2 bg-slate-400 rounded-full animate-bounce" style={{ animationDelay: '0ms' }}></div>
                  <div className="w-2 h-2 bg-slate-400 rounded-full animate-bounce" style={{ animationDelay: '150ms' }}></div>
                  <div className="w-2 h-2 bg-slate-400 rounded-full animate-bounce" style={{ animationDelay: '300ms' }}></div>
                </div>
              </div>
            </div>
          )}

          <div ref={messagesEndRef} />
        </div>

        {/* Suggestion Chips */}
        {suggestions.length > 0 && !isResponding && (
          <div data-testid="audia-suggestions" className="px-3 pt-2 pb-1 border-t border-slate-700/50 bg-slate-900/60">
            <div className="flex flex-wrap gap-1.5">
              {suggestions.map((q, idx) => (
                <button
                  key={idx}
                  data-testid={`audia-suggestion-${idx}`}
                  onClick={() => handleSuggestionTap(q)}
                  className="px-2.5 py-1 sm:px-3 sm:py-1.5 text-[11px] sm:text-xs text-slate-200 bg-slate-800 border border-slate-600/60 rounded-full hover:bg-teal-900/40 hover:border-teal-500/50 hover:text-teal-300 active:scale-95 transition-all duration-150 cursor-pointer leading-snug"
                >
                  {q}
                </button>
              ))}
            </div>
          </div>
        )}

        {/* Input Area */}
        <div className="border-t border-slate-700 bg-slate-900/90 p-3">
          {/* Controls Row */}
          <div className="mb-2 flex items-center justify-center gap-4">
            {/* Response Length Toggle (hidden in preview mode) */}
            {!isGuestSampleMode && (
            <>
            <label className="flex items-center gap-2 cursor-pointer">
              <button
                onClick={() => setResponseLength(responseLength === 'brief' ? 'detailed' : 'brief')}
                className={`relative inline-flex h-5 w-9 items-center rounded-full transition-colors ${
                  responseLength === 'detailed' ? 'bg-purple-600' : 'bg-slate-700'
                }`}
              >
                <span
                  className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                    responseLength === 'detailed' ? 'translate-x-5' : 'translate-x-0.5'
                  }`}
                />
              </button>
              <span className="flex items-center gap-1 text-xs text-slate-300 font-medium">
                <span className={responseLength === 'brief' ? 'text-slate-300' : 'text-purple-400'}>
                  {responseLength === 'brief' ? 'Detailed Answers' : 'Brief Answers'}
                </span>
              </span>
            </label>
            
            {/* Divider */}
            <div className="h-4 w-px bg-slate-600"></div>
            </>
            )}
            
            {/* TTS Toggle (visible for all modes including guest) */}
            <label className="flex items-center gap-2 cursor-pointer">
              <button
                onClick={() => setSpeakResponses(!speakResponses)}
                className={`relative inline-flex h-5 w-9 items-center rounded-full transition-colors ${
                  speakResponses ? 'bg-cyan-600' : 'bg-slate-700'
                }`}
              >
                <span
                  className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                    speakResponses ? 'translate-x-5' : 'translate-x-0.5'
                  }`}
                />
              </button>
              <span className="flex items-center gap-1.5 text-xs text-slate-300 font-medium">
                {speakResponses ? <Volume2 className="w-3.5 h-3.5 text-cyan-400" /> : <VolumeX className="w-3.5 h-3.5" />}
                Hear Audia
              </span>
            </label>
          </div>
          
          <div className="flex gap-2">
            <input
              ref={inputRef}
              type="text"
              value={inputValue}
              onChange={(e) => setInputValue(e.target.value)}
              onKeyDown={handleKeyDown}
              placeholder={isGuestSampleMode && previewQuestionCount >= 3 ? "Register to continue chatting with Audia" : "Ask Audia about the story..."}
              disabled={isResponding || (isGuestSampleMode && previewQuestionCount >= 3)}
              className="flex-1 px-4 py-2.5 bg-slate-800 border border-slate-700 rounded-xl text-white placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-cyan-500 focus:border-transparent disabled:opacity-50 text-sm"
            />
            <button
              onClick={() => handleSendMessage()}
              disabled={!inputValue.trim() || isResponding || (isGuestSampleMode && previewQuestionCount >= 3)}
              data-testid="audia-send-button"
              className="px-4 py-2.5 bg-gradient-to-br from-cyan-500 to-blue-600 hover:from-cyan-400 hover:to-blue-500 disabled:opacity-50 disabled:cursor-not-allowed text-white rounded-xl shadow-lg transition-all flex items-center justify-center"
              aria-label="Send message"
            >
              <Send className="w-5 h-5" />
            </button>
          </div>
          <p className="text-xs text-slate-500 mt-2 text-center">
            {speakResponses ? 'Voice responses enabled • ' : ''}Powered by NFD-SmartBook<sup className="text-[8px]">™</sup> technology
          </p>
          
          {/* Close Button */}
          <button
            onClick={handleCloseSession}
            className="mt-3 w-full px-4 py-2.5 bg-slate-800 hover:bg-slate-700 text-white rounded-xl border border-slate-700 transition-colors font-semibold text-sm"
          >
            Close Chat
          </button>
        </div>
        
        {/* Closing Spinner Overlay (Issue #2) */}
        {isClosing && (
          <div className="absolute inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 rounded-2xl">
            <div className="text-center">
              <div className="w-12 h-12 border-4 border-cyan-500 border-t-transparent rounded-full animate-spin mx-auto mb-3"></div>
              <p className="text-white font-semibold">Closing chat...</p>
            </div>
          </div>
        )}
      </div>

      {/* Session Summary Modal (Temporary Debug View) */}
      {showSummaryModal && sessionSummary && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/70 backdrop-blur-sm px-4">
          <div className="relative w-full max-w-md h-[680px] bg-gradient-to-br from-slate-900 to-slate-800 rounded-2xl shadow-2xl border border-slate-700 flex flex-col overflow-hidden">
            {/* Header - Fixed */}
            <div className="flex items-center justify-between px-6 py-4 border-b border-slate-700/50 flex-shrink-0">
              <h3 className="text-xl font-bold text-white flex items-center gap-2">
                <svg className="w-6 h-6 text-cyan-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                </svg>
                Session Summary
              </h3>
              <button
                onClick={handleDismissSummary}
                className="text-slate-400 hover:text-white transition-colors"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            {/* Scrollable Content */}
            <div className="flex-1 overflow-y-auto px-6 py-4 space-y-3">
              <div className="bg-slate-800/60 rounded-lg p-3 border border-slate-700/50">
                <div className="text-xs text-slate-500 mb-1">Session ID</div>
                <div className="text-sm text-slate-300 font-mono break-all">{sessionSummary.sessionId}</div>
              </div>

              <div className="bg-slate-800/60 rounded-lg p-3 border border-slate-700/50">
                <div className="text-xs text-slate-500 mb-1">Manuscript</div>
                <div className="text-sm text-white font-semibold">{sessionSummary.manuscriptTitle}</div>
                <div className="text-xs text-slate-400 font-mono">{sessionSummary.manuscriptId}</div>
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div className="bg-blue-900/30 rounded-lg p-3 border border-blue-700/40">
                  <div className="text-xs text-blue-400 mb-1">Questions Asked</div>
                  <div className="text-2xl font-bold text-blue-300">{sessionSummary.questionCount}</div>
                </div>
                <div className="bg-cyan-900/30 rounded-lg p-3 border border-cyan-700/40">
                  <div className="text-xs text-cyan-400 mb-1">Audia Responses</div>
                  <div className="text-2xl font-bold text-cyan-300">{sessionSummary.audiaMessageCount}</div>
                </div>
              </div>

              <div className="bg-purple-900/30 rounded-lg p-3 border border-purple-700/40">
                <div className="text-xs text-purple-400 mb-1">Credits Used</div>
                <div className="text-2xl font-bold text-purple-300">{sessionSummary.creditsUsed || Math.ceil((sessionSummary.activeEngagementUnits || 0) * 8)}</div>
              </div>

              {/* Narrative Summary - Featured Section */}
              {sessionSummary.narrativeSummary && (
                <div className="bg-gradient-to-br from-green-900/30 to-emerald-900/30 rounded-lg p-4 border border-green-700/40">
                  <div className="flex items-center gap-2 mb-2">
                    <svg className="w-5 h-5 text-green-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                    </svg>
                    <div className="text-sm font-semibold text-green-400">Conversation Summary</div>
                  </div>
                  <div className="text-sm text-slate-200 leading-relaxed">
                    {sessionSummary.narrativeSummary}
                  </div>
                </div>
              )}

              <div className="bg-slate-800/60 rounded-lg p-3 border border-slate-700/50">
                <div className="text-xs text-slate-500 mb-2">Session Duration</div>
                <div className="text-sm text-slate-300">
                  {Math.floor(sessionSummary.durationSeconds / 60)}m {sessionSummary.durationSeconds % 60}s
                </div>
              </div>

              <div className="bg-slate-800/60 rounded-lg p-3 border border-slate-700/50">
                <div className="text-xs text-slate-500 mb-2">Timestamps</div>
                <div className="space-y-1 text-xs text-slate-400 font-mono">
                  <div>Start: {new Date(sessionSummary.startedAt).toLocaleString()}</div>
                  <div>End: {new Date(sessionSummary.endedAt).toLocaleString()}</div>
                </div>
              </div>

              <div className="bg-amber-900/20 rounded-lg p-3 border border-amber-700/30">
                <div className="text-xs text-amber-400 mb-1">⚠️ Debug View</div>
                <div className="text-xs text-slate-400">
                  This modal is temporary and will be removed. Session data has been saved to the backend.
                </div>
              </div>
            </div>

            {/* Fixed Footer with Dismiss Button */}
            <div className="border-t border-slate-700/50 px-6 py-4 flex-shrink-0">
              <button
                onClick={handleDismissSummary}
                className="w-full px-4 py-2.5 bg-gradient-to-br from-cyan-500 to-blue-600 hover:from-cyan-400 hover:to-blue-500 text-white rounded-lg shadow-lg transition-all font-semibold"
              >
                Dismiss
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AudiaChatModal;
